/**
 * VOXEL 3D → GRID top-down footprint overlay (React/SVG).
 *
 * Draws the BIRD'S-EYE pixel map of a published 3D scene directly onto the Grid
 * so a 3D territory visibly OCCUPIES its space (instead of reading as an empty
 * parcel with only an "Enter 3D" pill). Each occupied world column is painted
 * with the colour of its highest voxel — a faithful top-down projection of the
 * voxel model. Positioned via the canonical camera math and clipped to the EXACT
 * parcel union (never a bbox), so it stays bound to the Territory. Rendered in
 * BOTH the intermediate and detail LOD bands (the caller passes `contentCam`,
 * which is non-null across both). Pointer-transparent so pan/zoom/selection pass
 * straight through to the Grid canvas; the interactive "Enter 3D" pill lives in
 * its own layer above this one.
 */
import { memo } from "react";

import { worldToScreen, type Camera } from "./camera";
import { unionScreenRects, type UnitRect } from "./brand-image-geometry";
import type { TopDownCell } from "../domain/voxel3d";

export interface Voxel3DFootprintItem {
  identityId: string;
  units: UnitRect[];
  cells: TopDownCell[];
}

const FootprintGroup = memo(function FootprintGroup({
  item,
  camera,
}: {
  item: Voxel3DFootprintItem;
  camera: Camera;
}) {
  const clip = unionScreenRects(camera, item.units);
  if (clip.length === 0) return null;
  const clipId = `voxel3dclip-${item.identityId}`;
  // Slight overlap (+1px, crisp edges) so adjacent cells never show a hairline
  // seam at fractional scales — the projection reads as a solid pixel map.
  const size = Math.ceil(camera.scale) + 1;
  return (
    <g data-testid={`grid-voxel3d-${item.identityId}`}>
      <defs>
        <clipPath id={clipId}>
          {clip.map((r, i) => (
            <rect key={i} x={r.x} y={r.y} width={r.w} height={r.h} />
          ))}
        </clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        {item.cells.map((c, i) => {
          const s = worldToScreen(camera, c.x, c.y);
          return <rect key={i} x={s.x} y={s.y} width={size} height={size} fill={c.color} />;
        })}
      </g>
    </g>
  );
});

/**
 * `camera` is non-null across the intermediate AND detail bands (the GridViewer
 * passes `contentCam = interCam ?? pubCam`), so at coarse overview zoom this
 * renders nothing and hands off cleanly to the adjacent LOD.
 */
export const Grid3DFootprintLayer = memo(function Grid3DFootprintLayer({
  camera,
  items,
}: {
  camera: Camera | null;
  items: Voxel3DFootprintItem[];
}) {
  if (!camera || items.length === 0) return null;
  return (
    <svg
      data-testid="grid-voxel3d-footprint-layer"
      shapeRendering="crispEdges"
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none", overflow: "hidden" }}
    >
      {items.map((item) => (
        <FootprintGroup key={item.identityId} item={item} camera={camera} />
      ))}
    </svg>
  );
});
